import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { api } from '../App';
import TTSBar from './TTSBar';
import VideoPlayer from './VideoPlayer';

const SUGGESTIONS = [
  '🎵 Trouve moi du reggae des années 90',
  '🎸 Musique rock pour travailler',
  '😢 Chansons tristes en français',
  '💃 Musique afrobeat dansante',
  '🌊 Sons relaxants nature',
];

function fmtSec(s) {
  if (!s) return '';
  const m = Math.floor(s / 60), sec = String(s % 60).padStart(2, '0');
  return `${m}:${sec}`;
}

function useFavorites() {
  const [favs, setFavs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('hepardon_favs') || '[]'); } catch { return []; }
  });
  const save = (list) => { setFavs(list); localStorage.setItem('hepardon_favs', JSON.stringify(list)); };
  const toggle = (v) => {
    const exists = favs.find(f => f.id === v.id);
    save(exists ? favs.filter(f => f.id !== v.id) : [v, ...favs].slice(0, 50));
  };
  const isFav = (id) => favs.some(f => f.id === id);
  return { favs, toggle, isFav };
}

const renderText = (text) =>
  text.split('**').map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );

export default function AIPage() {
  const [messages, setMessages] = useState([
    { role: 'ai', text: '👋 Salut ! Je suis l\'IA de **hé pardon!!**\n\nDis-moi quelle musique tu cherches, je vais te trouver ça sur YouTube. Je peux aussi traduire des paroles et t\'expliquer le sens d\'une chanson !' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [playing, setPlaying] = useState(null);
  const msgEnd = useRef(null);
  const history = useRef([]);
  const { toggle, isFav } = useFavorites();

  useEffect(() => { msgEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    setPlaying(null);
    setMessages(m => [...m, { role: 'user', text: msg }]);
    setLoading(true);
    history.current.push({ role: 'user', content: msg });

    try {
      const r = await axios.post(`${api}/ai/chat`, { message: msg, history: history.current.slice(-8) });
      const { reply, searchResults } = r.data;
      history.current.push({ role: 'assistant', content: reply });
      setMessages(m => [...m, { role: 'ai', text: reply }]);
      if (searchResults?.length) setResults(searchResults);
    } catch {
      setMessages(m => [...m, { role: 'ai', text: 'Désolé, je suis indisponible. Réessaie dans un instant.' }]);
    } finally {
      setLoading(false);
    }
  };

  const copyUrl = (url) => { navigator.clipboard?.writeText(url).catch(() => {}); };

  return (
    <div className="page" style={{ paddingBottom: 0 }}>
      <div style={{ marginBottom: 12 }}>
        <div className="badge">🤖 IA Musicale</div>
      </div>

      {/* Lecteur intégré */}
      {playing && (
        <VideoPlayer
          key={playing.id}
          videoId={playing.id}
          title={playing.title}
          uploader={playing.uploader}
          onClose={() => setPlaying(null)}
          onFav={() => toggle(playing)}
          isFav={isFav(playing.id)}
          url={playing.url}
        />
      )}

      {messages.length === 1 && !playing && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>Suggestions :</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {SUGGESTIONS.map(s => (
              <button key={s} className="btn-secondary" style={{ textAlign: 'left' }} onClick={() => send(s.replace(/^[^\s]+ /, ''))}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="chat-container">
        <div className="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`chat-msg ${m.role}`}>
              <div>{renderText(m.text)}</div>
              {m.role === 'ai' && (
                <div style={{ marginTop: 6 }}>
                  <TTSBar text={m.text.replace(/\*\*/g, '')} lang="fr" inline />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="chat-msg ai" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="loader" /> Je cherche...
            </div>
          )}
          <div ref={msgEnd} />
        </div>

        {results.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div className="divider" />
            <div className="section-title">🎵 Résultats ({results.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {results.map(v => (
                <div key={v.id} className="search-card" style={playing?.id === v.id ? { borderColor: 'var(--purple)' } : {}}>
                  <div className="search-thumb" style={{ width: 100, cursor: 'pointer' }} onClick={() => setPlaying(playing?.id === v.id ? null : v)}>
                    <img src={v.thumbnail} alt={v.title} loading="lazy" style={{ height: 67 }} />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
                      <span style={{ fontSize: 20 }}>{playing?.id === v.id ? '⏹' : '▶'}</span>
                    </div>
                  </div>
                  <div className="search-card-info">
                    <div className="search-card-title" style={{ fontSize: 12 }}>{v.title}</div>
                    <div className="search-card-meta">👤 {v.uploader} · {v.duration_string || fmtSec(v.duration)}</div>
                    <div className="search-card-actions">
                      <button className="btn-xs primary" onClick={() => setPlaying(playing?.id === v.id ? null : v)}>
                        {playing?.id === v.id ? '⏹' : '▶'}
                      </button>
                      <button
                        className="btn-xs"
                        onClick={() => toggle(v)}
                        style={isFav(v.id) ? { borderColor: 'gold', color: 'gold' } : {}}
                      >
                        {isFav(v.id) ? '⭐' : '☆'}
                      </button>
                      <button className="btn-xs" onClick={() => copyUrl(v.url)}>📋</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="chat-input-row">
          <textarea
            className="chat-input"
            rows={1}
            placeholder="Demande-moi de la musique..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          <button className="btn-primary" onClick={() => send()} disabled={loading || !input.trim()}>
            {loading ? <span className="loader" /> : '↑'}
          </button>
        </div>
      </div>
    </div>
  );
}
